import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createBugReportsQueries(client: StoxlyClient) {
  return createQueryKeys('bugReports', {
    // ['bugReports', 'list', { query }]
    list: (query?: Query<'bugReportsAdminList'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.bugReportsAdminList({ queries: query, signal }),
    }),

    // ['bugReports', 'detail', id]
    detail: (id: string) => ({
      queryKey: [id],
      queryFn: ({ signal }) => client.bugReportsAdminGetOne({ params: { id }, signal }),
    }),

    // ['bugReports', 'replyTemplate', id, { query }]
    //
    // Keyed on the status and category the admin has SELECTED, not the ones stored on the report:
    // the panel offers the draft while the triage form is still unsaved, so a key built from the
    // row would serve the message for the state the report is leaving.
    replyTemplate: (id: string, query: Query<'bugReportsAdminReplyTemplate'>) => ({
      queryKey: [id, { query }],
      queryFn: ({ signal }) =>
        client.bugReportsAdminReplyTemplate({ params: { id }, queries: query, signal }),
    }),

    // ['bugReports', 'attachmentDownload', attachmentId] — a presigned URL, so it is a QUERY by
    // HTTP verb but has a 15-minute lifetime; consumers fetch it on demand rather than caching it.
    attachmentDownload: (attachmentId: string) => ({
      queryKey: [attachmentId],
      queryFn: ({ signal }) =>
        client.bugReportsAdminDownloadAttachment({ params: { attachmentId }, signal }),
    }),

    // ['bugReports', 'attachmentsZip', id, origin] — a zip body; the admin panel fetches it as a
    // Blob through its download client, since the validated client cannot return one.
    attachmentsZip: (id: string, query: Query<'bugReportsAdminDownloadAllAttachments'>) => ({
      queryKey: [id, query.origin ?? 'all'],
      queryFn: ({ signal }) =>
        client.bugReportsAdminDownloadAllAttachments({ params: { id }, queries: query, signal }),
    }),

    /*
     * THE REPORTER'S OWN SURFACE (the Support module). Keyed apart from the admin entries above
     * because they are different data: these carry `reporterStatus` and the signal state, and
     * never an admin note. Invalidating one must not invalidate the other.
     */

    // ['bugReports', 'mine']
    mine: {
      queryKey: null,
      queryFn: ({ signal }) => client.bugReportsList({ signal }),
    },

    // ['bugReports', 'mineDetail', number]
    mineDetail: (number: number) => ({
      queryKey: [number],
      queryFn: ({ signal }) => client.bugReportsDetail({ params: { number }, signal }),
    }),

    // ['bugReports', 'unreadCount'] — the sidebar badge.
    unreadCount: {
      queryKey: null,
      queryFn: ({ signal }) => client.bugReportsUnreadCount({ signal }),
    },

    /**
     * The AGENT read, the query half of `agentUpdate` below. No app calls it — the caller is a
     * tool holding `X-Agent-Token`, which reads a report before it moves it — and the entry
     * exists so the operation is covered and the key namespace stays complete.
     */
    agentReport: (number: number, token: string) => ({
      queryKey: [number],
      queryFn: ({ signal }) =>
        client.bugReportsAgentGetOne({
          params: { number },
          headers: { 'X-Agent-Token': token },
          signal,
        }),
    }),

    /*
     * ['bugReports', 'batches'] — which reply batches have been staged and which a deploy has
     * published. It answers a question about RELEASES, not about any one report, so it is its own
     * key: staging a batch invalidates this and the report list, and neither should refetch
     * because the other changed.
     */
    batches: {
      queryKey: null,
      queryFn: ({ signal }) => client.bugReportsAdminListBatches({ signal }),
    },
  });
}

export function createBugReportsMutations(client: StoxlyClient) {
  return {
    /**
     * Filing a report is a `multipart/form-data` POST: the generated client documents the text
     * fields but cannot carry the `files` part, exactly as `usersImportData`. Apps send the
     * FormData through their own raw multipart POST helper; this entry exists so the operation is
     * covered and the key namespace stays complete.
     */
    create: {
      mutationFn: (body: Body<'bugReportsCreate'>) => client.bugReportsCreate(body),
    },
    update: {
      mutationFn: ({ id, body }: { id: string; body: Body<'bugReportsAdminUpdate'> }) =>
        client.bugReportsAdminUpdate(body, { params: { id } }),
    },
    /**
     * Record a message actually sent to the reporter. APPEND-ONLY — there is no edit and no
     * delete, because the row is a record of what a customer received, not a draft.
     */
    addMessage: {
      mutationFn: ({ id, body }: { id: string; body: Body<'bugReportsAdminAddMessage'> }) =>
        client.bugReportsAdminAddMessage(body, { params: { id } }),
    },

    /**
     * Post a merged reply batch to the running backend as DRAFTS. The body is the batch file
     * verbatim — the panel bundles the same files the backend image does, and sending one
     * unchanged is what keeps the two from holding different ideas of the reply.
     *
     * Idempotent server-side: a second click keeps the drafts that are there, edits included.
     */
    stageBatch: {
      mutationFn: (body: Body<'bugReportsAdminStageBatch'>) =>
        client.bugReportsAdminStageBatch(body),
    },
    /**
     * Correct a staged draft in the window between the merge and the deploy. 404 for an unknown
     * id, 409 once it is published — a message the reporter has read is a record, not a draft.
     */
    updateDraft: {
      mutationFn: ({
        messageId,
        body,
      }: {
        messageId: string;
        body: Body<'bugReportsAdminUpdateDraft'>;
      }) => client.bugReportsAdminUpdateDraft(body, { params: { messageId } }),
    },

    /** The reporter answers, and only while the report asks them to (409 otherwise). */
    reply: {
      mutationFn: ({ number, body }: { number: number; body: Body<'bugReportsPostMessage'> }) =>
        client.bugReportsPostMessage(body, { params: { number } }),
    },
    /** "It no longer occurs" / "it still occurs". 409 when the status takes none, 429 in cooldown. */
    signal: {
      mutationFn: ({ number, body }: { number: number; body: Body<'bugReportsSignal'> }) =>
        client.bugReportsSignal(body, { params: { number } }),
    },
    markRead: {
      mutationFn: ({ number }: { number: number }) =>
        client.bugReportsMarkRead(undefined, { params: { number } }),
    },
    /**
     * A presigned URL for one of the reporter's own uploads. A GET by verb, a MUTATION here: the
     * URL lives 15 minutes and is asked for when the link is clicked, so caching it under a key
     * (as the admin's `attachmentDownload` query does) would only serve an expired one.
     */
    downloadAttachment: {
      mutationFn: ({ number, attachmentId }: { number: number; attachmentId: string }) =>
        client.bugReportsDownloadAttachment({ params: { number, attachmentId } }),
    },
    /**
     * The AGENT endpoint. It is not called from any app — the caller is a tool holding
     * `X-Agent-Token` — and the entry exists so the operation is covered and the key namespace
     * stays complete, like `create` above.
     */
    agentUpdate: {
      mutationFn: ({
        number,
        token,
        body,
      }: {
        number: number;
        token: string;
        body: Body<'bugReportsAgentUpdate'>;
      }) =>
        client.bugReportsAgentUpdate(body, {
          params: { number },
          headers: { 'X-Agent-Token': token },
        }),
    },
    /**
     * The AGENT endpoint a merged pull request calls, through the `bug-report-pending-release`
     * workflow. No app calls it either; the entry exists so the operation is covered.
     */
    agentPendingRelease: {
      mutationFn: ({
        token,
        body,
      }: {
        token: string;
        body: Body<'bugReportsAgentPendingRelease'>;
      }) => client.bugReportsAgentPendingRelease(body, { headers: { 'X-Agent-Token': token } }),
    },
  };
}
